/* eslint-disable import/no-cycle */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import {
  Button,
  ButtonType,
  Drawer,
  Row
} from 'syngenta-digital-cropwise-react-ui-kit';
import { Formik } from 'formik';

import ChevronLeftIcon from 'assets/chevron_left.svg';
import CloseIcon from 'assets/close.svg';
import useInventoryProduct from 'screens/ProductList/hooks/useInventoryProduct';
import {
  handleFertilizerProduct,
  handleMasterProduct,
  handleServiceProduct
} from 'screens/ProductList/helpers/handleSchemaCustomProducts';
import LoadingSpinner from 'components/LoadingSpinner';
import { DEFAULT_BLUR_TIMEOUT } from 'components/PriceUnitField/components/FormInputWithPrecisionDisplay';
import CustomProductListForm from '../CustomProductListForm';
import MasterListProductForm from '../MasterListProductForm';
import {
  EditCustomProductSchema,
  EditCustomProductSchemaForService
} from './editCustomProductSchemas';
import { AddMasterListProductSchema } from './addMasterListProductSchema';

const EditProductDrawer = ({
  zIndex,
  visible,
  suppressAnimation,
  mask,
  product,
  editProductHeader,
  saveText,
  handleEdit,
  onClose,
  isLoading,
  loadingMessage
}) => {
  const { fetchCustomProduct } = useInventoryProduct();
  const [customProduct, setCustomProduct] = useState(product);
  const [schema, setSchema] = useState();
  const [isSavePending, setIsSavePending] = useState(false);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    // saveTimeoutRef holds a timeout id, not a DOM node, so reading .current
    // in the cleanup (to clear whatever is currently pending) is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => clearTimeout(saveTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (!visible) {
      clearTimeout(saveTimeoutRef.current);
      setIsSavePending(false);
      setCustomProduct(); // reset on drawer close
      // eslint-disable-next-line no-param-reassign, react-hooks/exhaustive-deps
      product = undefined;
    }
    if (!product || (!product.custom && !product.isNew)) {
      handleMasterProduct(setSchema);
    } else if (product.type === 'SERVICE') {
      handleServiceProduct(product, setCustomProduct, setSchema);
    } else {
      handleFertilizerProduct(
        product,
        setCustomProduct,
        setSchema,
        fetchCustomProduct,
        visible
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, visible]);

  const schemaDefinition =
    {
      MASTER: AddMasterListProductSchema,
      FERTILIZER: EditCustomProductSchema,
      SERVICE: EditCustomProductSchemaForService
    }[schema] || Yup.object().shape({});

  return !product ? null : (
    <Formik
      enableReinitialize
      validationSchema={schemaDefinition}
      initialValues={product}
      onSubmit={handleEdit}
      validateOnBlur
    >
      {({ isSubmitting, handleSubmit, isValid }) => (
        <Drawer
          zIndex={zIndex}
          className={`product-drawer ${
            suppressAnimation ? 'instant-drawer' : ''
          }`}
          open={visible}
          placement="right"
          closable={false}
          mask={mask} // When false, caller manages dimming externally to avoid flicker during drawer swaps
          maskClosable={false}
          onClose={onClose}
          width="50%"
          footer={
            !isLoading && (
              <Row justify="space-between">
                <Button
                  type={ButtonType.outline}
                  className="px-3"
                  onClick={onClose}
                  aria-label="Back"
                  size="large"
                >
                  <Row>
                    <img className="mr-1" alt="plus" src={ChevronLeftIcon} />
                    <span>Back</span>
                  </Row>
                </Button>
                <Button
                  type={ButtonType.primary}
                  loading={isSubmitting || isSavePending}
                  disabled={!isValid || isSavePending}
                  onClick={() => {
                    // scenario: user hit save with a focused input
                    // we are using FormInputWithPrecisionDisplay component that uses onblur and focus events to update the value and display value
                    // so we need to use setTimeout to make sure the formik submit is called at the end of the event loop
                    // this is not the best solution, but it works, it's a known issue also in yield, need to be discussed and fixed
                    setIsSavePending(true);
                    document.activeElement?.blur(); // focus the button to trigger blur on any focused input
                    saveTimeoutRef.current = setTimeout(() => {
                      handleSubmit();
                      setIsSavePending(false);
                    }, DEFAULT_BLUR_TIMEOUT);
                  }}
                  size="large"
                >
                  {saveText}
                </Button>
              </Row>
            )
          }
        >
          {!isLoading ? (
            <div className="h-full">
              <div className="pt-4 pr-4 pb-5 pl-8">
                <Row justify="end">
                  <button type="button" aria-label="Close" onClick={onClose}>
                    <img alt="close" src={CloseIcon} />
                  </button>
                </Row>
                {product?.custom || product?.isNew ? (
                  <div>
                    <div className="text-xl font-semibold">
                      {editProductHeader}
                    </div>
                    {customProduct ? (
                      <CustomProductListForm
                        setSchema={setSchema}
                        product={customProduct}
                      />
                    ) : null}
                  </div>
                ) : (
                  <div>
                    <div className="ml-2 mb-5 text-xl font-semibold">
                      {editProductHeader}
                    </div>
                    <MasterListProductForm product={product} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <Row justify="end" className="absolute right-0 top-0 p-4">
                <button type="button" onClick={onClose}>
                  <img alt="close" src={CloseIcon} />
                </button>
              </Row>
              <LoadingSpinner message={loadingMessage} className="h-screen" />
            </div>
          )}
        </Drawer>
      )}
    </Formik>
  );
};
EditProductDrawer.defaultProps = {
  zIndex: 1000,
  saveText: 'Save',
  suppressAnimation: false,
  mask: undefined,
  loadingMessage: '',
  isLoading: false
};

EditProductDrawer.propTypes = {
  zIndex: PropTypes.number,
  suppressAnimation: PropTypes.bool,
  mask: PropTypes.bool,
  handleEdit: PropTypes.func.isRequired,
  product: PropTypes.shape({
    type: PropTypes.string,
    isNew: PropTypes.bool,
    name: PropTypes.string,
    productId: PropTypes.string,
    averagePricePerUnit: PropTypes.number,
    averagePricePerUnitUnit: PropTypes.string,
    density: PropTypes.number,
    custom: PropTypes.bool
  }).isRequired,
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  editProductHeader: PropTypes.string.isRequired,
  saveText: PropTypes.string,
  loadingMessage: PropTypes.string,
  isLoading: PropTypes.bool
};

export default EditProductDrawer;
